import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { SpineAnimationSequenceBuilder } from '@Core/Animation/SpineAnimationSequence';
import SpineWrapper from '@Core/Animation/SpineWrapper';
import { getPositionsFromWinningLine } from '@Core/API/Data/WinDataProcessor';
import { buildSelectionMaskFromPositions, createEmptySelectionMask } from '@Core/API/Utils/Utils';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { EDebugWinPresentationEvents, EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { WinPresentation } from '@Core/Win/WinPresentation';
import { WinPresentationEventSubscriptionsBuilder } from '@Core/Win/WinPresentationEventSubscriptionsBuilder';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ETurboModeLevels, IPowerLine, IScatters, ISpinOutcome, IWinLevel, IWinningLine } from '@GFTypes/GameStoreTypes';
import { EActionName } from '@Managers/Events/EActionName';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EWinType } from '@Managers/Redux/HttpState';
import WinSoundsManager from '@Managers/SoundManager/WinSoundsManager';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateConditions } from '@Managers/StateManager/StateConditions';
import { getCurrentTurboLevel } from '@Managers/TurboManager/TurboManager';
import { GameScene, IOdometerCheckpoint, IWinConfiguration, IWinPresentationConfig } from '..';
import { BaseWinLevelsConfigParser } from './BaseWinLevelsConfigParser';
import WinMultiplierPresentation, { IWinMultiplierPresentationConfig } from './WinMultiplierPresentation';
import { STATIC_JACKPOT_STEPS_CONFIG, WIN_PRESENTATION_STEPS_CONFIG } from './WinPresentation.constants';

export class BaseWin extends GameComponent {
    private readonly winLevelsManager: BaseWinLevelsConfigParser;
    private readonly winConfig: IWinConfiguration;
    private readonly winSoundsManager: WinSoundsManager;
    private readonly winMultiplierPresentation: WinMultiplierPresentation | undefined;
    private currentBet: number;
    private currentTotalWin: number;
    private currentFinalWinLevel: IWinLevel;
    private currentFinalWinLevelIndex: number;
    private currentMultiplier: number | undefined;

    constructor(
        scene: GameScene,
        private readonly winPresentation: WinPresentation,
        private readonly winSpine: SpineWrapper,
        private readonly winMultiplierPresentationConfig?: IWinMultiplierPresentationConfig
    ) {
        super(scene);
        this.winConfig = ConfigLoader.getConfiguration(EConfigurationNames.GAME).win;
        const winLevelsConfig = ConfigLoader.getConfiguration(EConfigurationNames.GAME).winLevels;
        this.winSoundsManager = this.scene.game.soundManager.winSoundsManager;
        this.winLevelsManager = new BaseWinLevelsConfigParser(winLevelsConfig, this.winConfig, this.winSoundsManager, scene, this.winSpine);
        EventsCenter.on(EGameEvents.SEND_ACTION_REQUEST, this.saveBetFromSpinResponse, this);
        EventsCenter.on(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_START, this.onDebugWinPresentationStart, this);
        EventsCenter.on(EDebugWinPresentationEvents.DEBUG_WINPRESENTATION_5OFAKIND_CHANGE, this.onDebugWinPresentation5OfAKindChange, this);
        EventsCenter.on(EGameStates.PRE_WIN_STATE, this.onPreWinState, this);
        EventsCenter.on(EGameStates.PRE_WIN_STEP_STATE, this.onPreWinStepState, this);
        EventsCenter.on(EGameStates.POST_WIN_STEP_STATE, this.onPostWinStepState, this);
        EventsCenter.on(EWinPresentationStates.PRE_WIN_PRESENTATION_STATE, this.onPreWinPresentationState, this);
        const builder = new WinPresentationEventSubscriptionsBuilder()
            .during(EGameStates.WIN_STATE, EGameStates.POST_WIN_STATE, EGameStates.PRE_GAMBLE_STATE, EGameStates.PRE_SPIN_STATE, EGameStates.SPIN_STATE)
            .on(EGameStates.WIN_STATE, this.onWinState, this)
            .on(EGameStates.POST_SPIN_STATE, winPresentation.terminateWinPresentation, winPresentation)
            .on(EGameStates.PRE_GAMBLE_STATE, winPresentation.terminateWinPresentation, winPresentation)
            .on(EGameStates.PRE_SPIN_STATE, this.onPreSpin, this)
            .on(EGameStates.SPIN_STATE, this.winSoundsManager.stopCurrentWinSound, this.winSoundsManager)
            .on(EGameEvents.TAKE, this.onTake, this)
            .on(EGameEvents.SKIP, this.onSkip, this)
            .on(EWinPresentationStates.PRE_WIN_PRESENTATION_STATE, this.winSoundsManager.playWinSound, this.winSoundsManager)
            .on(EWinPresentationStates.WIN_PRESENTATION_HOLD_STATE, this.onWinPresentationHold, this)
            .on(EWinPresentationStates.WIN_PRESENTATION_HIDDEN_STATE, this.winSoundsManager.onWinPresentationHidden, this.winSoundsManager);

        if (winMultiplierPresentationConfig) {
            this.winMultiplierPresentation = new WinMultiplierPresentation(
                this.scene,
                this.buildWinMultiplierAnimationSequence,
                winMultiplierPresentationConfig
            );
            builder.on(EGameEvents.WIN_MULTIPLIER_PRESENTATION_START, this.showWinMultiplier, this);
        }
        const subscriptions = builder.build();
        winPresentation.assignEventSubscriptions(...subscriptions);
    }

    onInit(): void {
        this.updateOdometerDurationsPerWinLevel();
        this.gameStore.on(EGameStoreKeys.TurboModeState, this.updateOdometerDurationsPerWinLevel, this);
        this.gameStore.on(EGameStoreKeys.SpinOutcome, this.onSpinResultReceived, this);
        this.gameStore.on(EGameStoreKeys.CurrentGameState, this.updateDebugAvailability, this);
    }

    private startWin() {
        const isJackpotWin = this.currentFinalWinLevel.name === EWinType.JACKPOT_WIN;
        const stepsConfig = isJackpotWin ? STATIC_JACKPOT_STEPS_CONFIG : WIN_PRESENTATION_STEPS_CONFIG;
        const winMultiplierPresentationCheckpoint = this.currentMultiplier
            ? this.winMultiplierPresentation?.getOdometerCheckpoint(this.currentTotalWin, this.currentMultiplier)
            : undefined;
        const odometerCheckpoints = [winMultiplierPresentationCheckpoint].filter((e): e is IOdometerCheckpoint => e !== undefined);

        const config: IWinPresentationConfig = {
            winPresentationSubLevels: this.currentFinalWinLevel.subLevels,
            odometerHoldDuration: this.currentFinalWinLevel.odometerHoldDuration ?? this.winConfig.odometer?.holdDuration,
            winPresentationStepsConfig: stepsConfig,
            spine: this.winSpine,
            odometerCheckpoints
        };
        this.winPresentation.startWin(this.currentBet, this.currentTotalWin, config);
    }

    private onSkip() {
        if (!this.winPresentation.isInsideCheckpoint()) {
            this.winPresentation.skipWinPresentation();
        }
        this.winMultiplierPresentation?.skip();
    }

    private async showWinMultiplier() {
        if (!this.currentMultiplier) return;
        await this.winMultiplierPresentation?.show(this.currentMultiplier);
    }

    private onPreWinState() {
        const { steps } = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        const lastWinPresentationStepIndex = steps.length - 1;
        const isLastWinPresentationStep = steps.length <= 1;
        this.gameStore.set(
            EGameStoreKeys.BaseWinPresentationState,
            { currentWinPresentationStepIndex: -1, lastWinPresentationStepIndex, isLastWinPresentationStep },
            true
        );
    }

    private onPreWinStepState() {
        let { currentWinPresentationStepIndex, lastWinPresentationStepIndex } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        currentWinPresentationStepIndex++;
        const isLastWinPresentationStep = currentWinPresentationStepIndex >= lastWinPresentationStepIndex;
        const spinResult = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        this.currentMultiplier = spinResult.steps[currentWinPresentationStepIndex].totalWinMultiplier;
        if (!this.currentMultiplier && this.winMultiplierPresentationConfig) this.currentMultiplier = 1;
        this.gameStore.set(EGameStoreKeys.BaseWinPresentationState, { currentWinPresentationStepIndex, isLastWinPresentationStep }, true);
        this.prepareWinningPositions(spinResult, currentWinPresentationStepIndex);
    }

    private onWinState() {
        const { isLastWinPresentationStep } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        if (!isLastWinPresentationStep) return;
        this.startWin();
    }

    private onPostWinStepState() {}

    private async onDebugWinPresentationStart({ winAmount }: { winAmount: number }): Promise<void> {
        const debugState = this.gameStore.get(EGameStoreKeys.DebugWinPresentationModeState);
        const currentState = this.gameStore.get(EGameStoreKeys.CurrentGameState);
        const isAlreadyRunning = debugState.active;
        const isIdle = currentState === EGameStates.IDLE_STATE;
        if (!isIdle && !isAlreadyRunning) return;
        const { powerLines: hasPowerlines, payLines } = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig);
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);
        const symbolsOnReel = this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
        const visibleSymbols = createEmptySelectionMask(numberOfReels, symbolsOnReel).map((reel) => reel.map(() => 1));
        const hasWinlines = !hasPowerlines && payLines.length > 0;
        const {
            value: { raw: currentBet }
        } = this.gameStore.get(EGameStoreKeys.BetState);

        if (isAlreadyRunning) {
            this.gameStore.set(EGameStoreKeys.DebugWinPresentationModeState, { nextWinPresentationRequested: true }, true);
            EventsCenter.once(EGameStates.PRE_WIN_STATE, () =>
                this.gameStore.set(EGameStoreKeys.DebugWinPresentationModeState, { nextWinPresentationRequested: false }, true)
            );
        } else {
            this.gameStore.set(EGameStoreKeys.DebugWinPresentationModeState, { active: true }, true);
            EventsCenter.once(EGameStates.IDLE_STATE, () => {
                this.gameStore.set(EGameStoreKeys.DebugWinPresentationModeState, { active: false }, true);
                this.gameStore.set(EGameStoreKeys.IsTakeCompleted, true);
            });
        }

        this.gameStore.set(EGameStoreKeys.LastSpinBet, currentBet);

        const createWinningLines = () =>
            payLines.map((_, i) => ({
                direction: 1,
                id: i,
                symbolsCount: numberOfReels,
                win: 777,
                positions: getPositionsFromWinningLine(_, numberOfReels, 1),
                symbolId: 1,
                selectionMask: buildSelectionMaskFromPositions(getPositionsFromWinningLine(_, numberOfReels, 1), visibleSymbols),
                winMessageMultiplier: 1
            }));

        const createPowerLines = () =>
            Array.from({ length: symbolsOnReel }, (_, rowIndex) => {
                const positions = Array.from({ length: numberOfReels }, (_, reelIndex) => [reelIndex, rowIndex]) as [number, number][];
                return {
                    positions,
                    win: 777,
                    symbolId: 1,
                    numberOfLines: numberOfReels,
                    numberOfReels,
                    selectionMask: buildSelectionMaskFromPositions(positions, visibleSymbols),
                    winMessageMultiplier: 1
                };
            });

        const createStep = () => {
            const finalSymbols = Array.from({ length: numberOfReels }, () => Array.from({ length: symbolsOnReel + 2 }, () => 1));
            const finalVisibleSymbols = Array.from({ length: numberOfReels }, () => Array.from({ length: symbolsOnReel }, () => 1));

            if (hasWinlines) {
                return {
                    winningLines: createWinningLines(),
                    finalSymbols,
                    finalVisibleSymbols: finalVisibleSymbols,
                    win: winAmount
                };
            }

            return {
                powerLines: createPowerLines(),
                finalSymbols,
                finalVisibleSymbols: finalVisibleSymbols,
                win: winAmount
            };
        };

        const createSpinOutcome = () => ({
            win: winAmount,
            steps: [createStep()]
        });

        this.gameStore.set(EGameStoreKeys.SpinOutcome, createSpinOutcome(), true);

        EventsCenter.emit(EGameEvents.TAKE);
        if (isIdle) StateConditions.idleCondition.resolve();
    }

    private onDebugWinPresentation5OfAKindChange({ ofAKindModifier }: { ofAKindModifier: number | undefined }) {
        this.gameStore.set(EGameStoreKeys.DebugWinPresentationModeState, { ofAKindModifier }, true);
    }

    private updateDebugAvailability() {
        const { active, available } = this.gameStore.get(EGameStoreKeys.DebugWinPresentationModeState);
        const currentState = this.gameStore.get(EGameStoreKeys.CurrentGameState);
        const isIdleState = currentState === EGameStates.IDLE_STATE;
        const newAvailable = active || isIdleState;
        if (newAvailable === available) return;
        this.gameStore.set(EGameStoreKeys.DebugWinPresentationModeState, { available: newAvailable }, true);
    }

    private onTake(options?: { keepWinPresentation?: boolean }): void {
        this.winSoundsManager.stopCurrentWinSound();
        const keepWinPresentation = options?.keepWinPresentation ?? false;
        const currentGameState = this.gameStore.get(EGameStoreKeys.CurrentGameState);

        if (currentGameState === EGameStates.WIN_STATE) {
            StateConditions.winCondition.resolve();
            StateConditions.postWinCondition.resolve();
        }

        if (currentGameState === EGameStates.POST_WIN_STATE) {
            StateConditions.postWinCondition.resolve();
        }

        if (!keepWinPresentation) this.winPresentation.terminateWinPresentation();
    }

    private onPreSpin(): void {
        const turboState = this.gameStore.get(EGameStoreKeys.TurboModeState);
        const isTurbo = turboState.active && getCurrentTurboLevel(this.gameStore) === ETurboModeLevels.TURBO;
        if (isTurbo) return this.winPresentation.terminateWinPresentation();
        const { currentState } = this.gameStore.get(EGameStoreKeys.WinPresentation);
        if (currentState !== EWinPresentationStates.WIN_PRESENTATION_HOLD_STATE) return this.winPresentation.terminateWinPresentation();
        const newDelay = this.winPresentation.winConfig.odometer?.hideAfterSpinDelay ?? 0;
        this.winPresentation.winPresentationPromises.startTimer(newDelay);
    }

    private onWinPresentationHold(): void {
        const currentState = this.gameStore.get(EGameStoreKeys.CurrentGameState);
        if (currentState === EGameStates.WIN_STATE) StateConditions.winCondition.resolve();
    }

    public async onSpinResultReceived(): Promise<void> {
        const spinResult = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        this.prepareWinningPositions(spinResult);
        this.gameStore.set(EGameStoreKeys.IsTakeInitiated, false);
        this.gameStore.set(EGameStoreKeys.HighestSymbolCountOnPayLine, this.getHighestPayingSymbolCount());
        this.currentBet = this.gameStore.get(EGameStoreKeys.LastSpinBet)!;
        this.currentTotalWin = this.gameStore.get(EGameStoreKeys.SpinOutcome).win ?? 0;
        this.currentFinalWinLevelIndex = this.winLevelsManager.getWinLevels().findIndex((level) => level.to * this.currentBet > this.currentTotalWin);
        this.currentFinalWinLevel = this.winLevelsManager.getWinLevels()[this.currentFinalWinLevelIndex];
        const currentFinalWinLevelName = this.currentFinalWinLevel?.name ?? EWinType.NORMAL_WIN;

        this.gameStore.set(
            EGameStoreKeys.BaseWinPresentationState,
            { finalWinLevelName: currentFinalWinLevelName, finalWinLevelIndex: this.currentFinalWinLevelIndex },
            true
        );
    }

    private prepareWinningPositions(data: ISpinOutcome, stepIndex: number = 0): void {
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);
        const numberOfSymbols = this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
        const winningPositions = Array.from({ length: numberOfReels }, () => Array.from({ length: numberOfSymbols }, () => false));
        this.processWinlines(winningPositions, data.steps.at(stepIndex)?.winningLines);
        this.processWays(winningPositions, data.steps.at(stepIndex)?.powerLines);
        this.processScatters(winningPositions, data.steps.at(stepIndex)?.scatters);
        this.gameStore.set(EGameStoreKeys.BaseWinPresentationState, { winningPositions }, true);
    }

    private processScatters(positions: boolean[][], scatters: IScatters[] | undefined): boolean[][] {
        return this.processWays(positions, scatters);
    }

    private processWinlines(positions: boolean[][], winlines: IWinningLine[] | undefined): boolean[][] {
        if (!winlines?.length) return positions;

        const payLines = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).payLines;

        winlines.forEach((winline) => {
            const payline = payLines[winline.id];
            if (!payline) return;

            const reelIndexes = this.getWinningReels(winline.symbolsCount, winline.direction, winline.reelIndexes);

            reelIndexes.forEach((reelIndex) => {
                const position = payline[reelIndex];
                if (position == null) return;

                positions[reelIndex][position] = true;
            });
        });

        return positions;
    }

    private getWinningReels(symbolsCount: number, direction: number, reelIndexes?: number[]): number[] {
        if (reelIndexes?.length) return reelIndexes;
        if (direction === 1) return Array.from({ length: symbolsCount }, (_, i) => i);
        return Array.from({ length: symbolsCount }, (_, i) => this.gameStore.get(EGameStoreKeys.NumberOfReels) - symbolsCount + i);
    }

    private processWays<U extends { positions: IPowerLine['positions'] }>(positions: boolean[][], ways: U[] | undefined): boolean[][] {
        if (!ways || ways.length === 0) return positions;

        ways.forEach((way) => {
            positions = way.positions.reduce((acc: boolean[][], [x, y]) => {
                acc[x][y] = true;
                return acc;
            }, positions);
        });

        return positions;
    }

    private getHighestPayingSymbolCount(): number {
        const spinResult = this.gameStore.get(EGameStoreKeys.SpinOutcome);

        let highestSymbolCountOnPayLine = 0;
        if (spinResult.steps.at(-1)?.winningLines) {
            spinResult.steps.at(-1)?.winningLines?.forEach((line) => {
                if (line.symbolsCount > highestSymbolCountOnPayLine) highestSymbolCountOnPayLine = line.symbolsCount;
            });
        }

        if (spinResult.steps.at(-1)?.powerLines) {
            spinResult.steps.at(-1)?.powerLines?.forEach((ways) => {
                if (ways.numberOfReels > highestSymbolCountOnPayLine) highestSymbolCountOnPayLine = ways.numberOfReels;
            });
        }

        return highestSymbolCountOnPayLine;
    }

    private async saveBetFromSpinResponse(action: { actionName: EActionName; bet: number }): Promise<void> {
        if (action.actionName !== EActionName.SPIN && action.actionName !== EActionName.BONUS_BUY_SPIN) return;
        if (!action.bet) return;
        this.gameStore.set(EGameStoreKeys.LastSpinBet, action.bet);
    }

    public updateOdometerDurationsPerWinLevel() {
        this.gameStore.set(
            EGameStoreKeys.BaseWinPresentationState,
            { odometerDurationsPerWinLevel: this.getOdometerDurationsPerWinLevel(this.winLevelsManager.getWinLevels()) },
            true
        );
    }

    private getOdometerDurationsPerWinLevel(winLevelsConfig: IWinLevel[]): number[] {
        return winLevelsConfig.map((level) => this.getOdometerDuration(level));
    }

    private getOdometerDuration(level: IWinLevel): number {
        const { odometerDuration, subLevels } = level;
        if (!subLevels) return this.getTurboDuration(odometerDuration ?? 0);
        let timeNeededForOdometerToComplete = 0;
        subLevels.forEach((subLevel) => {
            const inEventName = subLevel.name + '_IN';
            const inAnimationName = this.winSpine.eventManager.getAnimationNameForGameEvent(inEventName);
            if (inAnimationName && inEventName !== EGameEvents.WIN_IN) {
                timeNeededForOdometerToComplete += this.winSpine.getAnimationDuration(inAnimationName);
            }
            timeNeededForOdometerToComplete += this.getTurboDuration(subLevel.odometerDuration);
        });
        return timeNeededForOdometerToComplete;
    }

    private getTurboDuration(duration: number): number {
        const { active, winSpeedPerLevel } = this.gameStore.get(EGameStoreKeys.TurboModeState);
        const turboLevel = getCurrentTurboLevel(this.gameStore);
        if (active) duration /= winSpeedPerLevel[turboLevel];
        return duration;
    }

    private onPreWinPresentationState(): void {
        const lastWinStep = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps.at(-1);
        const multi = lastWinStep?.winMultiplier || lastWinStep?.wallMultiplier;
        if (multi && multi > 1) EventsCenter.emit(EGameEvents.WIN_X_$, { eventIds: [multi] });
    }

    private buildWinMultiplierAnimationSequence = (
        multiplier: number,
        spineWrapper: SpineWrapper,
        { skip, animationNames }: IWinMultiplierPresentationConfig
    ) => {
        const builder = new SpineAnimationSequenceBuilder({ spineWrapper, skip });

        animationNames.forEach((event, index) => {
            builder.step(event, {
                eventIds: [multiplier],
                handler: () => {
                    EventsCenter.emit(event, { eventIds: [multiplier] });
                    return Promise.resolve();
                }
            });
        });

        return builder.build();
    };
}
